import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Search, Save, Download, Upload, Trash2, Plus, X, ChevronLeft, ChevronRight,
  AlertTriangle, Lock, Unlock, Ban, AlertCircle, CheckCircle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Card type colors matching Yu-Gi-Oh card frames
const CARD_COLORS = {
  "Normal Monster": "#fde68a",
  "Effect Monster": "#fdba74",
  "Ritual Monster": "#60a5fa",
  "Fusion Monster": "#c084fc",
  "Synchro Monster": "#e5e7eb",
  "Xyz Monster": "#1f2937",
  "Link Monster": "#3b82f6",
  "Pendulum Monster": "#84cc16",
  "Spell Card": "#22d3ee",
  "Trap Card": "#f472b6",
};

// Banlist status colors
const BANLIST_COLORS = {
  forbidden: { bg: "#ef4444", border: "#dc2626", text: "Prohibida" },
  limited: { bg: "#eab308", border: "#ca8a04", text: "Limitada (1)" },
  semi_limited: { bg: "#f97316", border: "#ea580c", text: "Semi-Limitada (2)" },
  unlimited: { bg: "#22c55e", border: "#16a34a", text: "Ilimitada (3)" },
};

const DeckBuilderPage = () => {
  const { deckId } = useParams();
  const navigate = useNavigate();
  
  // Deck state
  const [deckName, setDeckName] = useState("Nuevo Deck");
  const [deckFormat, setDeckFormat] = useState("tcg_current");
  const [mainDeck, setMainDeck] = useState([]);
  const [sideDeck, setSideDeck] = useState([]);
  const [extraDeck, setExtraDeck] = useState([]);
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Banlist state
  const [banlistData, setBanlistData] = useState({ forbidden: [], limited: [], semi_limited: [] });
  const [banlistLoading, setBanlistLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Search state
  const [searchName, setSearchName] = useState("");
  const [searchDesc, setSearchDesc] = useState("");
  const [cardType, setCardType] = useState("");
  const [monsterType, setMonsterType] = useState("");
  const [attribute, setAttribute] = useState("");
  const [level, setLevel] = useState("");
  const [atkMin, setAtkMin] = useState("");
  const [atkMax, setAtkMax] = useState("");
  const [defMin, setDefMin] = useState("");
  const [defMax, setDefMax] = useState("");
  const [sortOrder, setSortOrder] = useState("name");
  
  // Search results
  const [searchResults, setSearchResults] = useState([]);
  const [searchPage, setSearchPage] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // UI state
  const [selectedCard, setSelectedCard] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [formats, setFormats] = useState([]);
  
  const CARDS_PER_PAGE = 50;

  useEffect(() => {
    loadFormats();
    if (deckId && deckId !== "new") {
      loadDeck(deckId);
    }
  }, [deckId]);

  // Load banlist when format changes
  useEffect(() => {
    loadBanlist(deckFormat);
  }, [deckFormat]);

  // Validate deck whenever it changes
  useEffect(() => {
    validateDeck();
  }, [mainDeck, sideDeck, extraDeck, banlistData]);

  const loadFormats = async () => {
    try {
      const res = await axios.get(`${API}/decks/formats`);
      setFormats(res.data.formats || []);
    } catch (e) {
      console.error("Error loading formats:", e);
    }
  };

  const loadBanlist = async (format) => {
    setBanlistLoading(true);
    try {
      // Map format ID to banlist endpoint
      const formatMap = {
        goat: "goat",
        edison: "edison",
        hat: "hat",
        speed_duel: "speed-duel",
        tcg_current: "tcg-actual"
      };
      
      const endpoint = formatMap[format] || "tcg-actual";
      const res = await axios.get(`${API}/${endpoint}/banlist`);
      
      const data = res.data;
      setBanlistData({
        forbidden: (data.forbidden || []).map(c => c.name),
        limited: (data.limited || data.limited_1 || []).map(c => c.name),
        semi_limited: (data.semi_limited || data.limited_2 || []).map(c => c.name)
      });
    } catch (e) {
      console.error("Error loading banlist:", e);
      setBanlistData({ forbidden: [], limited: [], semi_limited: [] });
    }
    setBanlistLoading(false);
  };

  const loadDeck = async (id) => {
    try {
      const res = await axios.get(`${API}/decks/${id}`, { withCredentials: true });
      const deck = res.data;
      setDeckName(deck.name || "Deck");
      setDeckFormat(deck.format || "tcg_current");
      setMainDeck(deck.main_deck || []);
      setSideDeck(deck.side_deck || []);
      setExtraDeck(deck.extra_deck || []);
      setIsPublic(deck.is_public || false);
    } catch (e) {
      console.error("Error loading deck:", e);
    }
  };

  // Get banlist status for a card
  const getCardBanlistStatus = (cardName) => {
    if (banlistData.forbidden.includes(cardName)) return "forbidden";
    if (banlistData.limited.includes(cardName)) return "limited";
    if (banlistData.semi_limited.includes(cardName)) return "semi_limited";
    return "unlimited";
  };

  // Get max copies allowed for a card
  const getMaxCopies = (cardName) => {
    const status = getCardBanlistStatus(cardName);
    if (status === "forbidden") return 0;
    if (status === "limited") return 1;
    if (status === "semi_limited") return 2;
    return 3;
  };

  // Count copies of a card across all deck sections
  const countCardCopies = (cardName) => {
    const allCards = [...mainDeck, ...sideDeck, ...extraDeck];
    return allCards.filter(c => c.name === cardName).length;
  };

  // Validate entire deck
  const validateDeck = () => {
    const errors = [];
    const allCards = [...mainDeck, ...sideDeck, ...extraDeck];
    const cardCounts = {};
    
    // Count all cards
    allCards.forEach(card => {
      cardCounts[card.name] = (cardCounts[card.name] || 0) + 1;
    });
    
    // Check each unique card
    Object.entries(cardCounts).forEach(([cardName, count]) => {
      const maxAllowed = getMaxCopies(cardName);
      const status = getCardBanlistStatus(cardName);
      
      if (status === "forbidden" && count > 0) {
        errors.push({
          type: "forbidden",
          card: cardName,
          message: `"${cardName}" está PROHIBIDA en este formato`,
          count,
          max: 0
        });
      } else if (count > maxAllowed) {
        errors.push({
          type: status,
          card: cardName,
          message: `"${cardName}" excede el límite (${count}/${maxAllowed})`,
          count,
          max: maxAllowed
        });
      }
    });
    
    // Check deck sizes
    if (mainDeck.length < 40) {
      errors.push({
        type: "warning",
        message: `Main Deck necesita al menos 40 cartas (${mainDeck.length}/40)`
      });
    }
    if (mainDeck.length > 60) {
      errors.push({
        type: "error",
        message: `Main Deck no puede tener más de 60 cartas (${mainDeck.length}/60)`
      });
    }
    if (extraDeck.length > 15) {
      errors.push({
        type: "error",
        message: `Extra Deck no puede tener más de 15 cartas (${extraDeck.length}/15)`
      });
    }
    if (sideDeck.length > 15) {
      errors.push({
        type: "error",
        message: `Side Deck no puede tener más de 15 cartas (${sideDeck.length}/15)`
      });
    }
    
    setValidationErrors(errors);
  };

  const searchCards = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      let url = `${API}/cards?skip=${page * CARDS_PER_PAGE}&limit=${CARDS_PER_PAGE}`;
      
      if (searchName) url += `&search=${encodeURIComponent(searchName)}`;
      if (cardType) url += `&card_type=${cardType}`;
      if (attribute) url += `&attribute=${attribute}`;
      if (level) url += `&level=${level}`;
      
      const res = await axios.get(url);
      let cards = res.data.cards || [];
      
      // Client-side filtering for additional fields
      if (searchDesc) {
        cards = cards.filter(c => c.desc?.toLowerCase().includes(searchDesc.toLowerCase()));
      }
      if (monsterType) {
        cards = cards.filter(c => c.race?.toLowerCase().includes(monsterType.toLowerCase()));
      }
      if (atkMin) {
        cards = cards.filter(c => c.atk !== undefined && c.atk >= parseInt(atkMin));
      }
      if (atkMax) {
        cards = cards.filter(c => c.atk !== undefined && c.atk <= parseInt(atkMax));
      }
      if (defMin) {
        cards = cards.filter(c => c.def !== undefined && c.def >= parseInt(defMin));
      }
      if (defMax) {
        cards = cards.filter(c => c.def !== undefined && c.def <= parseInt(defMax));
      }
      
      // Sort
      if (sortOrder === "name") {
        cards.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      } else if (sortOrder === "atk_desc") {
        cards.sort((a, b) => (b.atk || 0) - (a.atk || 0));
      } else if (sortOrder === "atk_asc") {
        cards.sort((a, b) => (a.atk || 0) - (b.atk || 0));
      } else if (sortOrder === "def_desc") {
        cards.sort((a, b) => (b.def || 0) - (a.def || 0));
      } else if (sortOrder === "level_desc") {
        cards.sort((a, b) => (b.level || 0) - (a.level || 0));
      }
      
      setSearchResults(cards);
      setTotalResults(res.data.total || 0);
      setSearchPage(page);
    } catch (e) {
      console.error("Error searching cards:", e);
    }
    setLoading(false);
  }, [searchName, searchDesc, cardType, monsterType, attribute, level, atkMin, atkMax, defMin, defMax, sortOrder]);

  const addCardToDeck = (card, section = "auto") => {
    const cardName = card.name;
    const currentCopies = countCardCopies(cardName);
    const maxCopies = getMaxCopies(cardName);
    const status = getCardBanlistStatus(cardName);
    
    // Check if card can be added
    if (status === "forbidden") {
      alert(`⛔ "${cardName}" está PROHIBIDA en formato ${deckFormat.toUpperCase()}`);
      return;
    }
    
    if (currentCopies >= maxCopies) {
      alert(`⚠️ Ya tienes el máximo de copias permitidas de "${cardName}" (${maxCopies})`);
      return;
    }
    
    const cardEntry = {
      card_id: card.id,
      name: card.name,
      type: card.type,
      image: card.card_images?.[0]?.image_url_small || card.card_images?.[0]?.image_url,
      atk: card.atk,
      def: card.def,
      level: card.level,
      attribute: card.attribute,
      race: card.race,
      quantity: 1,
      card_type: section
    };
    
    // Determine which section based on card type
    const isExtraDeck = card.type?.includes("Fusion") || card.type?.includes("Synchro") || 
                        card.type?.includes("Xyz") || card.type?.includes("Link");
    
    if (section === "extra" || (section === "auto" && isExtraDeck)) {
      if (extraDeck.length < 15) {
        setExtraDeck([...extraDeck, { ...cardEntry, card_type: "extra" }]);
      } else {
        alert("Extra Deck lleno (máximo 15 cartas)");
      }
    } else if (section === "side") {
      if (sideDeck.length < 15) {
        setSideDeck([...sideDeck, { ...cardEntry, card_type: "side" }]);
      } else {
        alert("Side Deck lleno (máximo 15 cartas)");
      }
    } else {
      if (mainDeck.length < 60) {
        setMainDeck([...mainDeck, { ...cardEntry, card_type: "main" }]);
      } else {
        alert("Main Deck lleno (máximo 60 cartas)");
      }
    }
  };

  const removeCardFromDeck = (index, section) => {
    if (section === "main") {
      setMainDeck(mainDeck.filter((_, i) => i !== index));
    } else if (section === "side") {
      setSideDeck(sideDeck.filter((_, i) => i !== index));
    } else if (section === "extra") {
      setExtraDeck(extraDeck.filter((_, i) => i !== index));
    }
  };

  const moveCard = (index, fromSection, toSection) => {
    let card;
    if (fromSection === "main") {
      card = mainDeck[index];
      setMainDeck(mainDeck.filter((_, i) => i !== index));
    } else if (fromSection === "side") {
      card = sideDeck[index];
      setSideDeck(sideDeck.filter((_, i) => i !== index));
    } else {
      card = extraDeck[index];
      setExtraDeck(extraDeck.filter((_, i) => i !== index));
    }
    
    if (card) {
      if (toSection === "main" && mainDeck.length < 60) {
        setMainDeck([...mainDeck, { ...card, card_type: "main" }]);
      } else if (toSection === "side" && sideDeck.length < 15) {
        setSideDeck([...sideDeck, { ...card, card_type: "side" }]);
      } else if (toSection === "extra" && extraDeck.length < 15) {
        setExtraDeck([...extraDeck, { ...card, card_type: "extra" }]);
      }
    }
  };

  const clearDeck = () => {
    if (window.confirm("¿Limpiar todo el deck?")) {
      setMainDeck([]);
      setSideDeck([]);
      setExtraDeck([]);
    }
  };

  const saveDeck = async () => {
    // Check for critical errors before saving
    const criticalErrors = validationErrors.filter(e => e.type === "forbidden" || e.type === "error");
    if (criticalErrors.length > 0) {
      const proceed = window.confirm(
        `⚠️ Tu deck tiene ${criticalErrors.length} error(es):\n\n` +
        criticalErrors.map(e => `• ${e.message}`).join("\n") +
        `\n\n¿Deseas guardar de todos modos?`
      );
      if (!proceed) return;
    }
    
    setSaving(true);
    try {
      const deckData = {
        name: deckName,
        format: deckFormat,
        main_deck: mainDeck,
        side_deck: sideDeck,
        extra_deck: extraDeck,
        is_public: isPublic
      };
      
      if (deckId && deckId !== "new") {
        await axios.put(`${API}/decks/${deckId}`, deckData, { withCredentials: true });
        alert("✅ Deck guardado exitosamente");
      } else {
        const res = await axios.post(`${API}/decks`, deckData, { withCredentials: true });
        alert("✅ Deck creado exitosamente");
        navigate(`/deck-builder/${res.data.deck.id}`);
      }
    } catch (e) {
      alert("❌ Error al guardar: " + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  const exportDeck = () => {
    const lines = [`#created by Nirvana TCG - ${deckName}`, "#main"];
    mainDeck.forEach(c => lines.push(String(c.card_id)));
    lines.push("#extra");
    extraDeck.forEach(c => lines.push(String(c.card_id)));
    lines.push("!side");
    sideDeck.forEach(c => lines.push(String(c.card_id)));
    
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${deckName.replace(/\s+/g, "_")}.ydk`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getCardTypeColor = (card) => {
    if (!card?.type) return "#666";
    for (const [type, color] of Object.entries(CARD_COLORS)) {
      if (card.type.includes(type.replace(" Card", ""))) return color;
    }
    return "#666";
  };

  // Count cards by type
  const monsterCount = mainDeck.filter(c => c.type?.includes("Monster")).length;
  const spellCount = mainDeck.filter(c => c.type?.includes("Spell")).length;
  const trapCount = mainDeck.filter(c => c.type?.includes("Trap")).length;

  // Error counts
  const forbiddenCount = validationErrors.filter(e => e.type === "forbidden").length;
  const limitedErrors = validationErrors.filter(e => e.type === "limited" || e.type === "semi_limited").length;

  return (
    <div className="min-h-screen bg-[#1a1a2e] pt-20 pb-4" data-testid="deck-builder-page">
      <div className="flex h-[calc(100vh-6rem)]">
        {/* Left Panel - Controls */}
        <div className="w-52 bg-[#2d2d44] p-3 flex flex-col gap-2 border-r border-purple-900/50 overflow-y-auto">
          <h2 className="text-white font-bold text-center mb-2 text-sm">DECK BUILDER</h2>
          
          {/* Deck Name Input */}
          <input
            type="text"
            value={deckName}
            onChange={(e) => setDeckName(e.target.value)}
            className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
            placeholder="Nombre del deck"
            data-testid="deck-name-input"
          />
          
          {/* Format Selector */}
          <div>
            <label className="text-gray-400 text-xs">Formato:</label>
            <select
              value={deckFormat}
              onChange={(e) => setDeckFormat(e.target.value)}
              className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              data-testid="format-select"
            >
              {formats.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>
          
          {/* Banlist Loading Indicator */}
          {banlistLoading && (
            <div className="text-xs text-yellow-400 text-center animate-pulse">
              Cargando banlist...
            </div>
          )}
          
          <hr className="border-gray-600 my-1" />
          
          {/* Action Buttons */}
          <button onClick={exportDeck} className="deck-btn" data-testid="export-btn">
            <Download size={14} /> Exportar YDK
          </button>
          <button onClick={clearDeck} className="deck-btn text-red-400 hover:bg-red-900/30" data-testid="clear-btn">
            <Trash2 size={14} /> Limpiar Deck
          </button>
          
          <hr className="border-gray-600 my-1" />
          
          <button 
            onClick={saveDeck} 
            disabled={saving}
            className="deck-btn bg-green-600 hover:bg-green-500 disabled:opacity-50" 
            data-testid="save-btn"
          >
            <Save size={14} /> {saving ? "Guardando..." : "Guardar Deck"}
          </button>
          
          <button 
            onClick={() => setIsPublic(!isPublic)} 
            className={`deck-btn ${isPublic ? "bg-blue-600" : ""}`}
          >
            {isPublic ? <Unlock size={14} /> : <Lock size={14} />}
            {isPublic ? "Público" : "Privado"}
          </button>
          
          <hr className="border-gray-600 my-1" />
          
          {/* Deck Stats */}
          <div className="text-xs space-y-1">
            <div className="flex justify-between text-gray-400">
              <span>Main Deck:</span>
              <span className={`font-bold ${mainDeck.length < 40 ? "text-red-400" : mainDeck.length > 60 ? "text-red-400" : "text-white"}`}>
                {mainDeck.length}/60
              </span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>Extra Deck:</span>
              <span className={`font-bold ${extraDeck.length > 15 ? "text-red-400" : "text-white"}`}>
                {extraDeck.length}/15
              </span>
            </div>
            <div className="flex justify-between text-gray-400">
              <span>Side Deck:</span>
              <span className={`font-bold ${sideDeck.length > 15 ? "text-red-400" : "text-white"}`}>
                {sideDeck.length}/15
              </span>
            </div>
          </div>
          
          <hr className="border-gray-600 my-1" />
          
          {/* Card Type Counts */}
          <div className="text-xs space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ background: "#fdba74" }}></div>
              <span className="text-gray-400">Monsters:</span>
              <span className="text-white font-bold ml-auto">{monsterCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ background: "#22d3ee" }}></div>
              <span className="text-gray-400">Spells:</span>
              <span className="text-white font-bold ml-auto">{spellCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ background: "#f472b6" }}></div>
              <span className="text-gray-400">Traps:</span>
              <span className="text-white font-bold ml-auto">{trapCount}</span>
            </div>
          </div>
          
          <hr className="border-gray-600 my-1" />
          
          {/* Validation Status */}
          <div 
            className={`p-2 rounded cursor-pointer transition-colors ${
              validationErrors.length === 0 
                ? "bg-green-600/20 border border-green-500/50" 
                : forbiddenCount > 0 
                  ? "bg-red-600/20 border border-red-500/50"
                  : "bg-yellow-600/20 border border-yellow-500/50"
            }`}
            onClick={() => setShowValidation(!showValidation)}
          >
            <div className="flex items-center gap-2 text-xs">
              {validationErrors.length === 0 ? (
                <>
                  <CheckCircle size={14} className="text-green-400" />
                  <span className="text-green-400 font-medium">Deck Legal</span>
                </>
              ) : forbiddenCount > 0 ? (
                <>
                  <Ban size={14} className="text-red-400" />
                  <span className="text-red-400 font-medium">{forbiddenCount} prohibida(s)</span>
                </>
              ) : (
                <>
                  <AlertTriangle size={14} className="text-yellow-400" />
                  <span className="text-yellow-400 font-medium">{validationErrors.length} aviso(s)</span>
                </>
              )}
            </div>
          </div>
          
          {/* Validation Details */}
          {showValidation && validationErrors.length > 0 && (
            <div className="bg-gray-800/50 rounded p-2 text-xs space-y-1 max-h-32 overflow-y-auto">
              {validationErrors.map((err, idx) => (
                <div key={idx} className={`flex items-start gap-1 ${
                  err.type === "forbidden" ? "text-red-400" : 
                  err.type === "error" ? "text-red-400" :
                  err.type === "warning" ? "text-yellow-400" : 
                  "text-orange-400"
                }`}>
                  <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                  <span>{err.message}</span>
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-auto">
            <button 
              onClick={() => navigate("/decks")} 
              className="deck-btn w-full"
            >
              <ChevronLeft size={14} /> Volver a Decks
            </button>
          </div>
        </div>

        {/* Center - Deck Sections */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Main Deck */}
          <div className="flex-1 bg-gradient-to-b from-purple-900/40 to-purple-900/20 border-b-2 border-purple-500 overflow-auto">
            <div className="flex items-center gap-2 px-3 py-1 bg-purple-800/50 sticky top-0 z-10">
              <span className="text-white font-bold text-sm">MAIN DECK</span>
              <span className={`text-xs ${mainDeck.length < 40 ? "text-red-400" : "text-purple-300"}`}>
                ({mainDeck.length}/60) {mainDeck.length < 40 && "- Mínimo 40"}
              </span>
            </div>
            <div className="p-2 grid grid-cols-10 gap-1 auto-rows-max">
              {mainDeck.map((card, idx) => (
                <DeckCard 
                  key={idx} 
                  card={card} 
                  onClick={() => setSelectedCard(card)}
                  onRemove={() => removeCardFromDeck(idx, "main")}
                  onMoveToSide={() => moveCard(idx, "main", "side")}
                  borderColor={getCardTypeColor(card)}
                  banlistStatus={getCardBanlistStatus(card.name)}
                  copyCount={countCardCopies(card.name)}
                  maxCopies={getMaxCopies(card.name)}
                />
              ))}
              {mainDeck.length < 60 && (
                <div className="w-12 h-16 border-2 border-dashed border-purple-500/30 rounded flex items-center justify-center text-purple-500/50 text-xs">
                  +
                </div>
              )}
            </div>
          </div>

          {/* Side Deck */}
          <div className="h-28 bg-gradient-to-b from-blue-900/40 to-blue-900/20 border-b-2 border-blue-500 overflow-auto">
            <div className="flex items-center gap-2 px-3 py-1 bg-blue-800/50 sticky top-0 z-10">
              <span className="text-white font-bold text-sm">SIDE DECK</span>
              <span className="text-blue-300 text-xs">({sideDeck.length}/15)</span>
            </div>
            <div className="p-2 flex gap-1 flex-wrap">
              {sideDeck.map((card, idx) => (
                <DeckCard 
                  key={idx} 
                  card={card} 
                  onClick={() => setSelectedCard(card)}
                  onRemove={() => removeCardFromDeck(idx, "side")}
                  onMoveToMain={() => moveCard(idx, "side", "main")}
                  borderColor={getCardTypeColor(card)}
                  banlistStatus={getCardBanlistStatus(card.name)}
                  copyCount={countCardCopies(card.name)}
                  maxCopies={getMaxCopies(card.name)}
                />
              ))}
            </div>
          </div>

          {/* Extra Deck */}
          <div className="h-28 bg-gradient-to-b from-orange-900/40 to-orange-900/20 overflow-auto">
            <div className="flex items-center gap-2 px-3 py-1 bg-orange-800/50 sticky top-0 z-10">
              <span className="text-white font-bold text-sm">EXTRA DECK</span>
              <span className="text-orange-300 text-xs">({extraDeck.length}/15)</span>
            </div>
            <div className="p-2 flex gap-1 flex-wrap">
              {extraDeck.map((card, idx) => (
                <DeckCard 
                  key={idx} 
                  card={card} 
                  onClick={() => setSelectedCard(card)}
                  onRemove={() => removeCardFromDeck(idx, "extra")}
                  borderColor={getCardTypeColor(card)}
                  banlistStatus={getCardBanlistStatus(card.name)}
                  copyCount={countCardCopies(card.name)}
                  maxCopies={getMaxCopies(card.name)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Search */}
        <div className="w-80 bg-[#4a7c59] flex flex-col border-l border-green-900/50">
          <div className="p-2 bg-[#3d6a4a]">
            <h3 className="text-white font-black text-lg text-center">BUSCADOR DE CARTAS</h3>
          </div>
          
          <div className="p-2 space-y-2 overflow-y-auto max-h-[40vh]">
            {/* Name */}
            <div>
              <label className="text-white text-xs font-medium">Nombre:</label>
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchCards(0)}
                className="w-full px-2 py-1 bg-white border border-gray-400 rounded text-black text-sm"
                placeholder="Buscar por nombre..."
                data-testid="search-name"
              />
            </div>
            
            {/* Description */}
            <div>
              <label className="text-white text-xs font-medium">Descripción:</label>
              <input
                type="text"
                value={searchDesc}
                onChange={(e) => setSearchDesc(e.target.value)}
                className="w-full px-2 py-1 bg-white border border-gray-400 rounded text-black text-sm"
                placeholder="Buscar en texto..."
                data-testid="search-desc"
              />
            </div>
            
            {/* Card Type & Monster Type */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-white text-xs font-medium">Tipo de Carta:</label>
                <select
                  value={cardType}
                  onChange={(e) => setCardType(e.target.value)}
                  className="w-full px-1 py-1 bg-white border border-gray-400 rounded text-black text-xs"
                >
                  <option value="">Todos</option>
                  <option value="monster">Monster</option>
                  <option value="spell">Spell</option>
                  <option value="trap">Trap</option>
                </select>
              </div>
              <div>
                <label className="text-white text-xs font-medium">Tipo Monster:</label>
                <select
                  value={monsterType}
                  onChange={(e) => setMonsterType(e.target.value)}
                  className="w-full px-1 py-1 bg-white border border-gray-400 rounded text-black text-xs"
                >
                  <option value="">Todos</option>
                  <option value="Dragon">Dragon</option>
                  <option value="Warrior">Warrior</option>
                  <option value="Spellcaster">Spellcaster</option>
                  <option value="Fiend">Fiend</option>
                  <option value="Machine">Machine</option>
                  <option value="Zombie">Zombie</option>
                  <option value="Beast">Beast</option>
                  <option value="Beast-Warrior">Beast-Warrior</option>
                  <option value="Dinosaur">Dinosaur</option>
                  <option value="Fairy">Fairy</option>
                  <option value="Insect">Insect</option>
                  <option value="Aqua">Aqua</option>
                  <option value="Pyro">Pyro</option>
                  <option value="Thunder">Thunder</option>
                  <option value="Rock">Rock</option>
                  <option value="Plant">Plant</option>
                  <option value="Winged Beast">Winged Beast</option>
                  <option value="Reptile">Reptile</option>
                  <option value="Fish">Fish</option>
                  <option value="Sea Serpent">Sea Serpent</option>
                  <option value="Psychic">Psychic</option>
                  <option value="Wyrm">Wyrm</option>
                  <option value="Cyberse">Cyberse</option>
                </select>
              </div>
            </div>
            
            {/* Attribute & Level */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-white text-xs font-medium">Atributo:</label>
                <select
                  value={attribute}
                  onChange={(e) => setAttribute(e.target.value)}
                  className="w-full px-1 py-1 bg-white border border-gray-400 rounded text-black text-xs"
                >
                  <option value="">Todos</option>
                  <option value="DARK">DARK</option>
                  <option value="LIGHT">LIGHT</option>
                  <option value="EARTH">EARTH</option>
                  <option value="WATER">WATER</option>
                  <option value="FIRE">FIRE</option>
                  <option value="WIND">WIND</option>
                  <option value="DIVINE">DIVINE</option>
                </select>
              </div>
              <div>
                <label className="text-white text-xs font-medium">Nivel/Rank:</label>
                <select
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                  className="w-full px-1 py-1 bg-white border border-gray-400 rounded text-black text-xs"
                >
                  <option value="">Todos</option>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(l => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* ATK Range */}
            <div>
              <label className="text-white text-xs font-medium">ATK:</label>
              <div className="grid grid-cols-2 gap-1">
                <input
                  type="number"
                  value={atkMin}
                  onChange={(e) => setAtkMin(e.target.value)}
                  className="w-full px-1 py-1 bg-white border border-gray-400 rounded text-black text-xs"
                  placeholder="Min"
                />
                <input
                  type="number"
                  value={atkMax}
                  onChange={(e) => setAtkMax(e.target.value)}
                  className="w-full px-1 py-1 bg-white border border-gray-400 rounded text-black text-xs"
                  placeholder="Max"
                />
              </div>
            </div>
            
            {/* DEF Range */}
            <div>
              <label className="text-white text-xs font-medium">DEF:</label>
              <div className="grid grid-cols-2 gap-1">
                <input
                  type="number"
                  value={defMin}
                  onChange={(e) => setDefMin(e.target.value)}
                  className="w-full px-1 py-1 bg-white border border-gray-400 rounded text-black text-xs"
                  placeholder="Min"
                />
                <input
                  type="number"
                  value={defMax}
                  onChange={(e) => setDefMax(e.target.value)}
                  className="w-full px-1 py-1 bg-white border border-gray-400 rounded text-black text-xs"
                  placeholder="Max"
                />
              </div>
            </div>
            
            {/* Order */}
            <div>
              <label className="text-white text-xs font-medium">Ordenar por:</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-1 py-1 bg-white border border-gray-400 rounded text-black text-xs"
              >
                <option value="name">Nombre (A-Z)</option>
                <option value="atk_desc">ATK (Mayor a Menor)</option>
                <option value="atk_asc">ATK (Menor a Mayor)</option>
                <option value="def_desc">DEF (Mayor a Menor)</option>
                <option value="level_desc">Nivel (Mayor a Menor)</option>
              </select>
            </div>
            
            {/* Search Button */}
            <div className="flex gap-2">
              <button
                onClick={() => searchCards(searchPage > 0 ? searchPage - 1 : 0)}
                disabled={searchPage === 0}
                className="px-2 py-1.5 bg-[#6b8f71] hover:bg-[#7da383] rounded text-white disabled:opacity-50"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => searchCards(0)}
                className="flex-1 px-3 py-1.5 bg-[#8fbc8f] hover:bg-[#9fc99f] rounded text-black font-bold text-sm flex items-center justify-center gap-1"
                data-testid="search-btn"
              >
                <Search size={14} />
                Buscar
              </button>
              <button
                onClick={() => searchCards(searchPage + 1)}
                disabled={searchResults.length < CARDS_PER_PAGE}
                className="px-2 py-1.5 bg-[#6b8f71] hover:bg-[#7da383] rounded text-white disabled:opacity-50"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            
            <div className="text-center text-white text-xs">
              {totalResults > 0 ? `${searchPage * CARDS_PER_PAGE + 1} - ${Math.min((searchPage + 1) * CARDS_PER_PAGE, totalResults)} / ${totalResults}` : "0 resultados"}
            </div>
          </div>
          
          {/* Search Results Grid */}
          <div className="flex-1 bg-[#5a8c5a] p-2 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-3 border-t-white border-green-700 rounded-full animate-spin"></div>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="flex items-center justify-center h-full text-white/50 text-sm">
                Usa el buscador para encontrar cartas
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-1">
                {searchResults.map((card, idx) => {
                  const status = getCardBanlistStatus(card.name);
                  const copies = countCardCopies(card.name);
                  const maxCopies = getMaxCopies(card.name);
                  
                  return (
                    <div
                      key={idx}
                      className={`relative cursor-pointer group ${status === "forbidden" ? "opacity-50" : ""}`}
                      onClick={() => addCardToDeck(card, "auto")}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setSelectedCard(card);
                      }}
                      title={`${card.name}\n${BANLIST_COLORS[status].text}\nEn deck: ${copies}/${maxCopies}`}
                    >
                      <img
                        src={card.card_images?.[0]?.image_url_small || card.card_images?.[0]?.image_url}
                        alt={card.name}
                        className={`w-full rounded border-2 transition-all ${
                          status === "forbidden" ? "border-red-500 grayscale" :
                          status === "limited" ? "border-yellow-500" :
                          status === "semi_limited" ? "border-orange-500" :
                          "border-transparent group-hover:border-yellow-400"
                        }`}
                        loading="lazy"
                      />
                      
                      {/* Banlist Status Indicator */}
                      {status !== "unlimited" && (
                        <div 
                          className="absolute top-0 left-0 w-4 h-4 rounded-br text-[10px] font-bold flex items-center justify-center text-white"
                          style={{ background: BANLIST_COLORS[status].bg }}
                        >
                          {status === "forbidden" ? "⛔" : status === "limited" ? "1" : "2"}
                        </div>
                      )}
                      
                      {/* Level indicator */}
                      {card.level && (
                        <div className="absolute bottom-0 right-0 bg-black/80 text-yellow-400 text-xs px-1 rounded-tl font-bold">
                          {card.level}
                        </div>
                      )}
                      
                      {/* Copy count */}
                      {copies > 0 && (
                        <div className="absolute top-0 right-0 bg-purple-600 text-white text-[10px] px-1 rounded-bl font-bold">
                          {copies}x
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Card Preview Modal */}
      {selectedCard && (
        <CardPreviewModal 
          card={selectedCard} 
          onClose={() => setSelectedCard(null)}
          banlistStatus={getCardBanlistStatus(selectedCard.name)}
          onAddToMain={() => { addCardToDeck(selectedCard, "main"); setSelectedCard(null); }}
          onAddToSide={() => { addCardToDeck(selectedCard, "side"); setSelectedCard(null); }}
          onAddToExtra={() => { addCardToDeck(selectedCard, "extra"); setSelectedCard(null); }}
        />
      )}
      
      <style>{`
        .deck-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.25rem;
          padding: 0.5rem;
          background: #4a4a6a;
          border: 1px solid #6a6a8a;
          border-radius: 0.25rem;
          color: white;
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.2s;
        }
        .deck-btn:hover {
          background: #5a5a7a;
        }
      `}</style>
    </div>
  );
};

// Individual card in deck with banlist indicator
const DeckCard = ({ card, onClick, onRemove, onMoveToSide, onMoveToMain, borderColor, banlistStatus, copyCount, maxCopies }) => {
  const [showMenu, setShowMenu] = useState(false);
  
  const isOverLimit = copyCount > maxCopies;
  const statusColor = BANLIST_COLORS[banlistStatus];
  
  return (
    <div 
      className="relative group"
      onContextMenu={(e) => {
        e.preventDefault();
        setShowMenu(!showMenu);
      }}
    >
      <img
        src={card.image || card.card_images?.[0]?.image_url_small}
        alt={card.name}
        className={`w-12 h-16 object-cover rounded cursor-pointer border-2 transition-transform group-hover:scale-110 group-hover:z-10 ${
          isOverLimit ? "ring-2 ring-red-500" : ""
        } ${banlistStatus === "forbidden" ? "grayscale" : ""}`}
        style={{ borderColor: isOverLimit ? "#ef4444" : borderColor || "#666" }}
        onClick={onClick}
        loading="lazy"
      />
      
      {/* Banlist indicator */}
      {banlistStatus !== "unlimited" && (
        <div 
          className="absolute -top-1 -left-1 w-4 h-4 rounded-full text-[8px] font-bold flex items-center justify-center text-white shadow"
          style={{ background: statusColor.bg }}
          title={statusColor.text}
        >
          {banlistStatus === "forbidden" ? "⛔" : banlistStatus === "limited" ? "1" : "2"}
        </div>
      )}
      
      {/* Over limit warning */}
      {isOverLimit && (
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full text-white text-[8px] flex items-center justify-center animate-pulse">
          !
        </div>
      )}
      
      {/* Quick remove button */}
      {!isOverLimit && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
          className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          ×
        </button>
      )}
      
      {/* Level indicator */}
      {card.level && (
        <div className="absolute bottom-0 right-0 bg-black/80 text-yellow-400 text-[10px] px-0.5 rounded-tl font-bold">
          {card.level}
        </div>
      )}
      
      {/* Context Menu */}
      {showMenu && (
        <div className="absolute top-full left-0 z-50 bg-gray-800 rounded shadow-lg py-1 min-w-28">
          <button
            onClick={() => { onRemove?.(); setShowMenu(false); }}
            className="w-full px-3 py-1 text-left text-xs text-red-400 hover:bg-gray-700"
          >
            Eliminar
          </button>
          {onMoveToSide && (
            <button
              onClick={() => { onMoveToSide(); setShowMenu(false); }}
              className="w-full px-3 py-1 text-left text-xs text-blue-400 hover:bg-gray-700"
            >
              Mover a Side
            </button>
          )}
          {onMoveToMain && (
            <button
              onClick={() => { onMoveToMain(); setShowMenu(false); }}
              className="w-full px-3 py-1 text-left text-xs text-purple-400 hover:bg-gray-700"
            >
              Mover a Main
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Card Preview Modal with add options
const CardPreviewModal = ({ card, onClose, banlistStatus, onAddToMain, onAddToSide, onAddToExtra }) => {
  const statusColor = BANLIST_COLORS[banlistStatus];
  const isExtraDeck = card.type?.includes("Fusion") || card.type?.includes("Synchro") || 
                      card.type?.includes("Xyz") || card.type?.includes("Link");
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 rounded-xl p-4 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex gap-4">
          <img
            src={card.image || card.card_images?.[0]?.image_url}
            alt={card.name}
            className="w-40 rounded-lg"
          />
          <div className="flex-1">
            <h3 className="text-white font-bold text-lg">{card.name}</h3>
            <p className="text-gray-400 text-sm">{card.type}</p>
            
            {/* Banlist Status Badge */}
            <div 
              className="inline-block mt-2 px-2 py-1 rounded text-xs font-bold"
              style={{ background: statusColor.bg, color: "white" }}
            >
              {statusColor.text}
            </div>
            
            {card.level && <p className="text-yellow-400 text-sm mt-2">Nivel: {card.level}</p>}
            {card.attribute && <p className="text-blue-400 text-sm">Atributo: {card.attribute}</p>}
            {card.race && <p className="text-green-400 text-sm">Tipo: {card.race}</p>}
            {(card.atk !== undefined || card.def !== undefined) && (
              <p className="text-gray-300 text-sm">ATK/{card.atk ?? "?"} DEF/{card.def ?? "?"}</p>
            )}
          </div>
        </div>
        
        {card.desc && (
          <p className="mt-3 text-gray-300 text-sm max-h-24 overflow-y-auto border-t border-gray-700 pt-2">{card.desc}</p>
        )}
        
        {/* Add buttons */}
        {banlistStatus !== "forbidden" && (
          <div className="flex gap-2 mt-4">
            {!isExtraDeck && (
              <>
                <button
                  onClick={onAddToMain}
                  className="flex-1 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-semibold text-sm"
                >
                  + Main Deck
                </button>
                <button
                  onClick={onAddToSide}
                  className="flex-1 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-semibold text-sm"
                >
                  + Side Deck
                </button>
              </>
            )}
            {isExtraDeck && (
              <button
                onClick={onAddToExtra}
                className="flex-1 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg font-semibold text-sm"
              >
                + Extra Deck
              </button>
            )}
          </div>
        )}
        
        {banlistStatus === "forbidden" && (
          <div className="mt-4 p-3 bg-red-900/50 border border-red-500 rounded-lg text-center">
            <Ban size={24} className="mx-auto text-red-400 mb-1" />
            <p className="text-red-400 text-sm font-medium">Esta carta está PROHIBIDA en este formato</p>
          </div>
        )}
        
        <button
          onClick={onClose}
          className="mt-4 w-full py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};

export default DeckBuilderPage;
